import { useCallback, useEffect, useState } from 'react';
import { Toast } from 'common-package';
import { Checkbox, Input } from 'antd';
import { klona } from 'klona';
import cx from './index.module.scss';

interface CheckBoxProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  options?: string[];
  name: 'ADJUSTMENT_PURPOSE' | 'PHYSICAL_ASSESSMENT';
  othersText: string;
}

enum PlaceHolderText {
  ADJUSTMENT_PURPOSE = '可输入顾客其他调理诉求',
  PHYSICAL_ASSESSMENT = '可输入顾客其他身体情况',
}

type CompareResult = {
  valid: boolean;
  invalidItems: {
    index: number;
    value: string;
  }[];
};

const sortArray = (arr1: string[], arr2: string[]) => {
  arr1.sort((a, b) => {
    let aIndex = arr2.indexOf(a);
    let bIndex = arr2.indexOf(b);

    if (aIndex === -1) {
      return 1; // 如果a不在arr2中，则放在最后
    }
    if (bIndex === -1) {
      return -1; // 如果b不在arr2中，则放在前面
    }

    return aIndex - bIndex; // 根据arr2中的顺序排序
  });
  return arr1;
};

const checkAssessment = (assessmentArray: string[], physicalAssessment: string[]) => {
  let result = { valid: true, invalidItems: [] } as CompareResult;

  assessmentArray?.forEach((value, index) => {
    if (!physicalAssessment?.includes(value)) {
      result.valid = false;
      result.invalidItems.push({ index: index, value: value });
    }
  });

  return result;
};

const OTHERS_OPTIONS = 'Others';

const CheckBox = (props: CheckBoxProps) => {
  const { value, onChange, options, name, othersText } = props;
  const [isShowTextArea, setIsShowTextArea] = useState<boolean>(false);
  const [checkBoxValue, setCheckBoxValue] = useState<string[]>(value);
  const [fieldValue, setFieldValue] = useState<string[]>(null);
  const [inputVal, setInputVal] = useState<string>('');

  const handleChange = useCallback(
    (value: string[]) => {
      setCheckBoxValue(value);
      if (value?.includes(OTHERS_OPTIONS)) {
        setIsShowTextArea(true);
        const newValue = klona(value);
        const index = newValue?.indexOf(OTHERS_OPTIONS);
        if (index !== -1) {
          newValue[index] = inputVal;
        }
        inputVal ? setFieldValue(newValue) : setFieldValue(value?.filter(item => item !== OTHERS_OPTIONS));
      } else {
        setIsShowTextArea(false);
        setFieldValue(value);
      }
    },
    [inputVal]
  );

  useEffect(() => {
    if (!!fieldValue) {
      onChange(sortArray(fieldValue, options));
    } else {
      // 编辑赋值
      let compareResult = checkAssessment(value, options);

      if (!compareResult.valid) {
        const othersInfo = compareResult?.invalidItems?.[0];
        const newValue = klona(value);
        newValue[othersInfo?.index] = OTHERS_OPTIONS;
        setCheckBoxValue(newValue);
        setIsShowTextArea(true);
        setInputVal(othersInfo?.value);
      } else {
        setCheckBoxValue(value);
        setIsShowTextArea(false);
      }
    }
  }, [fieldValue, inputVal, onChange, options, value]);

  return (
    <div className={cx('checkbox')}>
      <Checkbox.Group onChange={handleChange} value={checkBoxValue} defaultValue={value}>
        {options?.map(item => (
          <Checkbox key={item} value={item}>
            {item === OTHERS_OPTIONS ? othersText : item}
          </Checkbox>
        ))}
      </Checkbox.Group>
      {isShowTextArea && (
        <div className={cx('text-area')}>
          <Input.TextArea
            placeholder={PlaceHolderText[name]}
            onChange={event => {
              const newValue = klona(checkBoxValue);
              if (newValue?.length > 0) {
                const index = newValue?.indexOf(OTHERS_OPTIONS);
                if (index !== -1) {
                  newValue[index] = event.target.value;
                }
                setFieldValue(newValue);
              } else {
                setFieldValue(null);
              }
              if (event.target.value?.length > 40) {
                Toast.show('最多可填写40字符');
              } else {
                setInputVal(event.target.value);
              }
            }}
            value={inputVal}
          />
          <div className={cx('text-num')}>
            <span className={cx('current-num')}>{inputVal?.length || 0}</span>
            <span> / 40</span>
          </div>
        </div>
      )}
    </div>
  );
};

export { CheckBox };
